import { Dialog, DialogTitle, Divider, Box, Stack } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { ReactElement, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { formatTranslation } from 'intl';
import {
  CommonTranslationKeys,
  FieldTranslationKeys,
  InspectionTranslationKeys,
} from 'intl/keys';
import { useProjectTimezone } from 'store/project';
import { useSites } from 'store/site';
import { useLevelsToSelect } from 'store/level';
import { keepUndeleted } from 'shared/domain/deletable/filters';
import { toLabelledEntity } from 'helpers/misc';
import { getLevelsForMultipleSites } from 'presentation/level/levelExtractor';
import { DayMomentValidation } from 'shared/types/time';
import { nowTimezonedDateTime } from 'shared/utils/date/dates';
import {
  MemoStatefulDatepicker,
  MemoStatefulMultiselect,
} from 'components/dataCreationForms/withInputForm/statefulInputsWrapper';
import {
  WithInputForm,
  useInputForm,
} from 'components/dataCreationForms/withInputForm';
import { ButtonContained, ButtonText } from 'components/general';
import { InspectionFieldName } from 'components/inspection/Form/types';
import { InspectionTemplateItemModel } from 'components/dataProviders/withRequiredInspectionTemplate/types';
import { InspectionSubmitFormFields } from 'shared/domain/inspection/mapping/toOutDto';
import {
  createInspectionFormValidation,
  inspectionValidation,
  validateInspectionField,
} from 'views/inspections/wizard/validation';

const SUBMIT_EVENT_TYPE = 'start-inspection-dialog-submit';

const dialogRootSx: SxProps<Theme> = {
  '& .MuiDialog-paper': {
    margin: 16,
    width: '100%',
  },
};

const dialogTitleSx: SxProps<Theme> = {
  p: '16px',
  '& .MuiTypography-root': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    height: '18px',
  },
};

export interface StartInspectionDialogProps {
  open: boolean;
  template: InspectionTemplateItemModel | undefined;
  isSubmitting: boolean;
  onClose: () => void;
  onStart: (values: InspectionSubmitFormFields) => Promise<void>;
}

function StartInspectionDialogContent({
  timezone,
  levelsPerSite,
  availableSites,
  isSubmitting,
  onClose,
}: {
  timezone: string;
  levelsPerSite: ReturnType<typeof useLevelsToSelect>['levelsPerSite'];
  availableSites: ReturnType<typeof toLabelledEntity>[];
  isSubmitting: boolean;
  onClose: () => void;
}): ReactElement {
  const { dispatchSubmit } = useInputForm();
  const intl = useIntl();

  const executionDateProps = {
    formKey: 'inspectionDate',
    labelId: InspectionTranslationKeys.executionDate,
    fieldName: InspectionFieldName.inspectionDate,
    daymoment: DayMomentValidation.start,
    timezone,
    defaultValue: nowTimezonedDateTime(
      timezone,
      DayMomentValidation.start
    ),
    reserveSpaceForHelperText: true,
    dense: true,
    'data-qa': 'start-inspection-execution-date-field',
    issueParams: null,
  } as any;

  const sitesProps = {
    formKey: 'sites',
    labelId: FieldTranslationKeys.site,
    fieldName: InspectionFieldName.multiselectSites,
    available: availableSites,
    autoSelect: 'asMultiSelect',
    maxVisibleTags: 2,
    reserveSpaceForHelperText: true,
    dense: true,
    'data-qa': 'start-inspection-sites-field',
    issueParams: null,
  } as any;

  const levelsProps = {
    formKey: 'levels',
    labelId: FieldTranslationKeys.level,
    fieldName: InspectionFieldName.multiselectLevels,
    available: levelsPerSite,
    dependsOn: 'sites',
    extractor: getLevelsForMultipleSites,
    groupBy: (option: { group?: string }): string => option.group ?? '',
    autoSelect: 'asMultiSelect',
    reserveSpaceForHelperText: true,
    dense: true,
    'data-qa': 'start-inspection-levels-field',
    issueParams: null,
  } as any;

  return (
    <>
      <Box sx={{ p: 2, pt: 2 }}>
        <Stack spacing={1.5}>
          <MemoStatefulDatepicker {...executionDateProps} />

          <MemoStatefulMultiselect {...sitesProps} />

          <MemoStatefulMultiselect {...levelsProps} />
        </Stack>
      </Box>

      <Divider />

      <Box sx={{ p: 1.5 }}>
        <Stack direction='row' justifyContent='flex-end' spacing={0.75}>
          <ButtonText onClick={onClose} disabled={isSubmitting}>
            {formatTranslation(intl, CommonTranslationKeys.cancel)}
          </ButtonText>
          <ButtonContained
            onClick={() => dispatchSubmit()}
            disabled={isSubmitting}
          >
            {formatTranslation(intl, CommonTranslationKeys.buttonCreateInspection)}
          </ButtonContained>
        </Stack>
      </Box>
    </>
  );
}

export function StartInspectionDialog({
  open,
  template,
  isSubmitting,
  onClose,
  onStart,
}: StartInspectionDialogProps): ReactElement {
  const intl = useIntl();
  const timezone = useProjectTimezone();
  const { items: sites } = useSites();
  const { levelsPerSite } = useLevelsToSelect();

  const availableSites = useMemo(
    () => sites.filter(keepUndeleted).map(toLabelledEntity),
    [sites]
  );

  const initialValues = useMemo(
    () => ({
      inspectionDate: nowTimezonedDateTime(
        timezone,
        DayMomentValidation.start
      ),
      sites: undefined,
      levels: undefined,
    }),
    [timezone]
  );

  const dispatchSubmit = useCallback(() => {
    window.dispatchEvent(new CustomEvent(SUBMIT_EVENT_TYPE));
  }, []);

  const handleSubmit = useCallback(
    async (values: InspectionSubmitFormFields): Promise<void> => {
      await onStart(values);
    },
    [onStart]
  );

  if (!template) {
    return <></>;
  }

  return (
    <Dialog
      sx={dialogRootSx}
      open={open}
      keepMounted={false}
      onClose={onClose}
    >
      <DialogTitle sx={dialogTitleSx}>
        {formatTranslation(intl, CommonTranslationKeys.buttonCreateInspection)}
      </DialogTitle>
      <Divider />

      <WithInputForm
        values={initialValues}
        errors={{}}
        eventType={SUBMIT_EVENT_TYPE}
        handleFieldValidation={validateInspectionField}
        handleFormValidation={createInspectionFormValidation(
          inspectionValidation,
          template
        )}
        onSubmit={handleSubmit}
        dispatchSubmit={dispatchSubmit}
        formName='inspection'
      >
        <StartInspectionDialogContent
          timezone={timezone}
          levelsPerSite={levelsPerSite}
          availableSites={availableSites}
          isSubmitting={isSubmitting}
          onClose={onClose}
        />
      </WithInputForm>
    </Dialog>
  );
}
